import { useEffect, useState } from "react";

import {
  ACCOUNT_STATUS,
  BrandPermissionEntry,
  Brands,
  BrandType,
  ICreateBrand,
} from "@shared/lib/types";
import { Avatar, Button, Modal, Row, Select, Table, TableTitleLayout, Tag } from "@shared/ui";

import { Select as AntdSelect, Col, Drawer, Flex, Input } from "antd";
import { ColumnsType } from "antd/es/table";
import styles from "./styles.module.scss";

import { PlusOutlined } from "@ant-design/icons";
import { BrandForm, useCreateBrand, useGetBrands } from "@entities/brand";
import { ManageBrand } from "@features/ManageBrand/ManageBrand";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Search } from "lucide-react";

export function BrandManagement() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS[]>([
    ACCOUNT_STATUS.ACTIVE,
  ]);
  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string[]>([]);
  const [accountManagers, setAccountManagers] = useState<
    {
      _id: string;
      count: number;
      firstName: string;
      lastName: string;
      brandIds: string[];
    }[]
  >([]);

  const [brandType, setBrandType] = useState<BrandType[]>([]);

  const [selectedBrand, setSelectedBrand] = useState<string>("");

  // const [searchOrganisationName, setSearchOrganisationName] =
  //   useState<string>("");
  // const [debouncedSearchOrganisationName] = useDebounce(
  //   searchOrganisationName,
  //   1000
  // );

  const { brands, isFetching } = useGetBrands(
    pageSize,
    paegNo,
    searchQuery,
    accountStatus,
    brandType,
    accountManagers.flatMap((item) => item.brandIds),
    selectedCompanyName
  );

  const { create, isCreating, isCreated } = useCreateBrand();

  const [isModalVisible, setModalVisible] = useState(false);

  const accToDisplay =
    brands?.facets?.accountManagers?.map((item) => item) || [];
  const brandTypesToDisplay =
    brands?.facets?.type?.map((item) => item._id) || [];
  const paymentTerms =
    brands?.facets?.paymentTerms?.map((item) => item._id) || [];
  const statusToDisplay = brands?.facets?.status?.map((item) => item._id) || [];

  const handleCreateBrand = () => {
    setSelectedBrand("");
    setModalVisible(true);
  };

  const handleSubmit = (brandData: ICreateBrand) => {
    create(brandData);
  };
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };
  const handleStatusChange = (value: ACCOUNT_STATUS[] | undefined) => {
    if (!value) return setAccountStatus([]);
    setAccountStatus(value);
  };
  const handleCompanyNameChange = (value: string[] | undefined) => {
    if (!value) return setSelectedCompanyName([]);
    setSelectedCompanyName(value);
  };
  const handleAccManagersChange = (
    value:
      | {
          _id: string;
          count: number;
          firstName: string;
          lastName: string;
          brandIds: string[];
        }[]
      | undefined
  ) => {
    if (!value) return setAccountManagers([]);
    setAccountManagers(value);
  };
  const handleBrandTypeChange = (value: BrandType[] | undefined) => {
    if (!value) return setBrandType([]);
    setBrandType(value);
  };
  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  useEffect(() => {
    setPageNo(1);
  }, [
    pageSize,
    searchQuery,
    accountStatus,
    brandType,
    accountManagers,
    selectedCompanyName,
  ]);

  const columns: ColumnsType<Brands> = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      render: (_: string, record: Brands) => (
        <Row
          align="middle"
          gutter={[8, 8]}
          style={{ cursor: "pointer" }}
          onClick={() => setSelectedBrand(record.brandId)}
        >
          <Col>
            <Avatar src={record.logoUrl} alt="Profile Picture" size={40} />
          </Col>
          <Col>
            <TextWithTooltip text={record.name} limit={30} />
          </Col>
        </Row>
      ),
    },

    {
      title: "Organisation Name",
      dataIndex: "brandOrganisationName",
      key: "brandOrganisationName",
      render: (_: string, record: Brands) => (
        <TextWithTooltip text={record.brandOrganisationName} limit={30} />
      ),
    },
    {
      title: "Account Manager",
      dataIndex: "permission",
      key: "permission",
      filterCount: accountManagers.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const accManagersItems = accToDisplay.map((role) => ({
          value: role._id,
          name: `${role.firstName} ${role.lastName}`,
        }));
        const selectedIds = accountManagers.map((item) => item._id);

        return (
          <SelectFilter
            title="Account Manager"
            placeholder="Select Account Managers"
            itemsToDisplay={accManagersItems}
            selectedItems={selectedIds}
            handleSelectionChange={(selectedValues) => {
              const selectedObjects = accToDisplay.filter((item) =>
                selectedValues.includes(item._id)
              );
              handleAccManagersChange(selectedObjects);
            }}
            props={props}
          />
        );
      },
      render: (permission: BrandPermissionEntry[]) => {
        const renderedPermissions = permission.filter(
          (item) => item.userType !== "brand-user"
        );
        return (
          <div className={styles.accGrid}>
            {renderedPermissions.slice(0, 3).map((item) => (
              <div>
                {item.user.firstName} {item.user.lastName}
              </div>
            ))}
            {renderedPermissions.length > 3 && <div>...</div>}
          </div>
        );
      },
    },

    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: brandType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          placeholder="Select Brand Type"
          itemsToDisplay={brandTypesToDisplay}
          selectedItems={brandType}
          handleSelectionChange={(items) =>
            handleBrandTypeChange(items as BrandType[])
          }
          props={props}
        />
      ),
      render: (status: BrandType) => <Tag color={"cyan"}>{status}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filterCount: accountStatus.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Status"
          placeholder="Select status"
          itemsToDisplay={statusToDisplay}
          selectedItems={accountStatus}
          handleSelectionChange={(items) =>
            handleStatusChange(items as ACCOUNT_STATUS[])
          }
          props={props}
        />
      ),
      render: (status: ACCOUNT_STATUS) => (
        <Tag color={status === ACCOUNT_STATUS.ACTIVE ? "green" : "red"}>
          {status}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    if (isCreated) {
      setModalVisible(false);
    }
  }, [isCreated]);

  return (
    <div>
      <h1 className={styles.title}>Brands</h1>
        <Table
          dataSource={brands?.data}
          columns={columns}
          loading={isFetching}
          title={() => <TableTitleLayout 
            filterComponent={<Flex justify="space-between" style={{width: "100%"}}>
              <Input
            placeholder="Search by brand name or company name"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 350 }}
            prefix={<Search size={14}/>}
          />
          <Button
            icon={<PlusOutlined />}
            disabled={isCreating}
            type="primary"
            onClick={() => handleCreateBrand()}
          >
            Create Brand
          </Button>
            </Flex>}
            totalText={`${brands?.pagination?.totalCount || 0} Records found`}
          />}
          rowKey="_id"
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: brands?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
        />
      <Modal
        title={"Create Brand"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <BrandForm onSubmit={handleSubmit} isSubmitting={isCreating} />
      </Modal>
      <Drawer
        destroyOnClose
        title={"Brand"}
        open={Boolean(selectedBrand)}
        onClose={() => {
          setSelectedBrand("");
        }}
        footer={null}
        width={700}
      >
        {selectedBrand && (
          <ManageBrand
            brandId={selectedBrand}
            paymentTerms={paymentTerms}
            brandOrganisationId=""
          />
        )}
      </Drawer>
    </div>
  );
}
